import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Lock, BookmarkPlus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { oapi, fmtMoney, minorToInput } from '@/lib/officialsApi';
import { Field, Loading, Money, Panel } from './PayBits';
import { RateTarget, SCOPES, splitPair } from './RateTarget';
import { ApplyPastToggle, SameFeeFill, pastSummary } from './RateEditorBits';
import { NoFeesBanner, TemplatesCard } from './RateTemplates';
import { TemplateDialog } from './TemplateDialog';
import { ApplyTemplateDialog } from './ApplyTemplateDialog';

const LEVEL = { default: 'Default', sport: 'Sport', event: 'League', official: 'Official', official_event: 'Official + league' };
const ORDER = { official_event: 0, official: 1, event: 2, sport: 3, default: 4 };
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const RatesTable = ({ d, onPick }) => {
  const find = (list, id) => list.find(x => x.id === id)?.name || 'Removed';
  const name = (r) => (r.scope === 'default' ? 'Everyone' : r.scope === 'sport' ? cap(r.ref)
    : r.scope === 'official_event' ? `${find(d.officials, splitPair(r.ref)[0])} · ${find(d.events, splitPair(r.ref)[1])}`
      : find(r.scope === 'event' ? d.events : d.officials, r.ref));
  const role = (k) => d.roles.find(r => r.key === k)?.label || k;
  const rows = [...d.rates].sort((a, b) => ORDER[a.scope] - ORDER[b.scope] || name(a).localeCompare(name(b)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="rates-table">
        <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 text-left">
          <th className="px-4 py-2">Applies to</th><th className="px-2 py-2">Level</th><th className="px-2 py-2">Role</th><th className="px-2 py-2 text-right">Fee</th><th className="px-4 py-2">Updated</th>
        </tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} onClick={() => onPick(r.scope, r.ref || '')} className="border-t cursor-pointer hover:bg-slate-50" data-testid={`rate-row-${r.id}`}>
            <td className="px-4 py-2 font-semibold text-slate-800">{name(r)}</td>
            <td className="px-2 py-2"><span className="text-[11px] font-bold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{LEVEL[r.scope]}</span></td>
            <td className="px-2 py-2 text-slate-600">{role(r.role_key)}</td>
            <td className="px-2 py-2 text-right font-bold"><Money minor={r.amount_minor} currency={d.currency} /></td>
            <td className="px-4 py-2 text-xs text-slate-400 whitespace-nowrap">{(r.updated_at || '').slice(0, 10)}{r.updated_by ? ` · ${r.updated_by}` : ''}</td>
          </tr>
        ))}</tbody>
      </table>
      {!rows.length && <p className="py-10 text-center text-sm text-slate-500" data-testid="rates-empty">No rates yet. Set a default fee per role to get started.</p>}
    </div>
  );
};

export const RatesPanel = ({ orgId, perms, refresh, budgets, reloadMeta }) => {
  const [d, setD] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [scope, setScope] = useState('default');
  const [ref, setRef] = useState('');
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [past, setPast] = useState(false);
  const [tplFor, setTplFor] = useState(null);
  const [applyFor, setApplyFor] = useState(null);
  const loadRates = useCallback(() => oapi('/officials/payments/rates', { orgId }).then(setD).catch(e => toast.error(e.message)), [orgId]);
  const loadTemplates = useCallback(() => oapi('/officials/payments/rate-templates', { orgId }).then(r => setTemplates(r.templates)).catch(e => toast.error(e.message)), [orgId]);
  useEffect(() => { loadRates(); loadTemplates(); }, [loadRates, loadTemplates]);
  const refKey = scope === 'default' ? '' : ref;
  useEffect(() => {
    if (!d) return;
    const v = {};
    d.rates.filter(r => r.scope === scope && (r.ref || '') === refKey).forEach(r => { v[r.role_key] = minorToInput(r.amount_minor); });
    setVals(v);
  }, [d, scope, refKey]);
  if (!d) return <Loading />;
  const can = perms.has('rates');
  const defaults = Object.fromEntries(d.rates.filter(r => r.scope === 'default').map(r => [r.role_key, r.amount_minor]));
  const noFees = d.events.filter(e => !d.rates.some(r => r.scope === 'event' && r.ref === e.id));
  const ready = scope === 'default' || (scope === 'official_event' ? splitPair(ref).every(Boolean) : !!ref);
  const pick = (s, r) => { setScope(s); setRef(r); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await oapi('/officials/payments/rates', { method: 'PUT', orgId, body: { scope, ref: refKey, apply_past: past, rates: Object.fromEntries(d.roles.map(x => [x.key, vals[x.key] ?? ''])) } });
      setD(r);
      const head = r.changed ? `${r.changed} rate${r.changed === 1 ? '' : 's'} updated` : 'No rate changes';
      toast.success(r.past ? `${head} · ${pastSummary(r.past, m => fmtMoney(m, d.currency))}` : r.changed ? `${head} · unpaid fees recalculated` : head);
      setPast(false);
      refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const removeTemplate = (t) => oapi(`/officials/payments/rate-templates/${t.id}`, { method: 'DELETE', orgId })
    .then(() => { toast.success('Template deleted'); loadTemplates(); }).catch(e => toast.error(e.message));
  const applied = (r) => { setApplyFor(null); loadRates(); refresh(); if (r.budgets_set) reloadMeta(); };
  const saved = (t) => { setTplFor(null); loadTemplates(); if (applyFor) setApplyFor(a => ({ ...a, template_id: t.id })); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start [&>*]:min-w-0" data-testid="pay-rates">
      {noFees.length > 0 && <div className="lg:col-span-12"><NoFeesBanner events={noFees} can={can} onCopy={ids => setApplyFor({ event_ids: ids })} /></div>}
      <div className="lg:col-span-5 space-y-4"><Panel title="Set fees" desc="The most specific rate wins: official in a league → official → league → sport → default." testId="rates-editor">
        <div className="p-4 space-y-3">
          {!can && <p className="text-xs flex items-center gap-1.5 text-slate-500 bg-slate-50 rounded-lg px-3 py-2" data-testid="rates-readonly"><Lock className="w-3.5 h-3.5" />View only. Ask the owner for "Manage rates".</p>}
          <Field label="Applies to">
            <Select value={scope} onValueChange={v => pick(v, '')}>
              <SelectTrigger data-testid="rates-scope"><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <RateTarget d={d} scope={scope} refId={ref} onPick={pick} />
          {can && <SameFeeFill roles={d.roles} disabled={!ready} onFill={v => setVals(v)} />}
          <div className="space-y-2 pt-1">
            {d.roles.map(r => (
              <div key={r.key} className="flex items-center gap-3">
                <span className="flex-1 text-sm font-semibold text-slate-700">{r.label}</span>
                <Input type="number" min="0" step="0.01" disabled={!can || !ready} value={vals[r.key] ?? ''} onChange={e => setVals(v => ({ ...v, [r.key]: e.target.value }))}
                  placeholder={scope !== 'default' && defaults[r.key] !== undefined ? `Default ${fmtMoney(defaults[r.key], d.currency)}` : 'No fee'} className="w-40 h-9 text-right" data-testid={`rate-input-${r.key}`} />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">Leave a box empty to use the next rate down. Unless you tick the box below, approved or paid fees never change.</p>
          {can && perms.has('approve') && <ApplyPastToggle checked={past} disabled={!ready} onChange={setPast} />}
          {can && <div className="flex flex-wrap justify-end gap-2">
            {scope === 'event' && ref && <Button size="sm" variant="outline" onClick={() => setTplFor({ event_id: ref })} data-testid="rates-save-as-template"><BookmarkPlus className="w-4 h-4 mr-1.5" />Save as template</Button>}
            {scope === 'event' && ref && <Button size="sm" variant="outline" onClick={() => setApplyFor({ event_ids: [ref] })} data-testid="rates-apply-template"><Copy className="w-4 h-4 mr-1.5" />Copy from template</Button>}
            <Button size="sm" disabled={busy || !ready} onClick={save} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="rates-save-btn">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save fees</Button>
          </div>}
        </div>
      </Panel>
      <TemplatesCard templates={templates} roles={d.roles} currency={d.currency} events={d.events} can={can}
        onNew={() => setTplFor({})} onEdit={setTplFor} onDelete={removeTemplate} onApply={t => setApplyFor({ template_id: t.id })} /></div>
      <div className="lg:col-span-7"><Panel title="All rates" desc={`${d.rates.length} configured · tap one to edit`} testId="rates-list"><RatesTable d={d} onPick={pick} /></Panel></div>
      <TemplateDialog target={tplFor} orgId={orgId} roles={d.roles} events={d.events} rates={d.rates} budgets={budgets} currency={d.currency}
        onClose={() => setTplFor(null)} onSaved={saved} />
      <ApplyTemplateDialog target={tplFor ? null : applyFor} templates={templates} orgId={orgId} events={d.events} rates={d.rates} currency={d.currency}
        onClose={() => setApplyFor(null)} onApplied={applied} onCreateTemplate={() => setTplFor({})} />
    </div>
  );
};
